import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { topNavStyle } from "../components/nav/topNavStyle";
import { supabase } from "../supabase/supabase";
import BrandWordmark from "../components/BrandWordmark";
import { consumeAuthRedirect } from "../utils/authRedirect";

const inputStyle = {
  width: '100%',
  padding: '12px 16px',
  borderRadius: 12,
  border: '1.5px solid var(--color-border)',
  fontSize: 15,
  color: 'var(--color-text-strong)',
  outline: 'none',
  boxSizing: 'border-box',
  backgroundColor: 'var(--color-bg-page)',
};

const labelStyle = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--color-text-muted)',
  marginBottom: 6,
};

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data: { user }, error: signInError } = await supabase.auth.signInWithPassword({ email, password });
      if (signInError) throw signInError;

      const { data: userData } = await supabase.from("users").select("is_admin").eq("id", user.id).maybeSingle();
      if (userData?.is_admin) return navigate("/admin", { replace: true });

      navigate(consumeAuthRedirect() || "/home", { replace: true });
    } catch (err) {
      setError("Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="theme-auto" style={{ minHeight: '100vh', backgroundColor: 'var(--color-bg-card)', display: 'flex', flexDirection: 'column' }}>

      {/* Nav */}
      <nav style={topNavStyle({ paddingX: 24, paddingY: 16 })}>
        <Link to="/"><BrandWordmark width="clamp(100px, 25vw, 160px)" /></Link>
        <Link to="/signup" style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-cta)', textDecoration: 'none' }}>
          Sign up free
        </Link>
      </nav>

      {/* Form */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>
        <div style={{ width: '100%', maxWidth: 400 }}>
          <h1 style={{ fontSize: 26, fontWeight: 900, color: 'var(--color-text-strong)', marginBottom: 8 }}>Welcome back</h1>
          <p style={{ fontSize: 15, color: 'var(--color-text-muted)', marginBottom: 32 }}>Log in to manage your lineup.</p>

          {error && (
            <div style={{ backgroundColor: 'var(--color-danger-bg)', border: '1px solid var(--color-danger-border)', borderRadius: 10, padding: '12px 16px', marginBottom: 24 }}>
              <p style={{ fontSize: 14, color: 'var(--color-danger-text)', margin: 0 }}>{error}</p>
            </div>
          )}

          <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={labelStyle}>Email</label>
              <input
                type="email"
                name="email"
                id="email"
                autoComplete="username"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
                placeholder="your@email.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Password</label>
              <input
                type="password"
                name="password"
                id="current-password"
                autoComplete="current-password"
                placeholder="Enter your password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                style={inputStyle}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '14px 0', borderRadius: 50, border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                backgroundColor: loading ? 'var(--color-cta-disabled)' : 'var(--color-cta)', color: 'var(--color-cta-text)', fontWeight: 700, fontSize: 16, marginTop: 8,
              }}
            >
              {loading ? "Logging in..." : "Log in"}
            </button>
          </form>

          <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
            <Link to="/forgot-password" style={{ fontSize: 14, color: 'var(--color-cta)', textDecoration: 'none', fontWeight: 500 }}>
              Forgot your password?
            </Link>
            <p style={{ fontSize: 14, color: 'var(--color-text-muted)', margin: 0 }}>
              No account?{' '}
              <Link to="/signup" style={{ color: 'var(--color-cta)', fontWeight: 600, textDecoration: 'none' }}>Sign up free</Link>
            </p>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 24, flexWrap: 'wrap' }}>
            <Link to="/privacy" style={{ color: 'var(--color-text-faint)', fontSize: 13, textDecoration: 'none' }}>
              Privacy Policy
            </Link>
            <Link to="/terms" style={{ color: 'var(--color-text-faint)', fontSize: 13, textDecoration: 'none' }}>
              Terms of Service
            </Link>
            <Link to="/support" style={{ color: 'var(--color-text-faint)', fontSize: 13, textDecoration: 'none' }}>
              Support
            </Link>
            <Link to="/delete-account" style={{ color: 'var(--color-text-faint)', fontSize: 13, textDecoration: 'none' }}>
              Delete Account
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
